import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bell, 
  Check, 
  X,
  BookOpen,
  Award,
  MessageCircle,
  Calendar,
  ShoppingBag,
  AlertTriangle,
  Info,
  CheckCircle,
  Filter,
  MoreVertical
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';

interface Notification {
  id: string;
  type: 'course' | 'achievement' | 'message' | 'reminder' | 'order' | 'system';
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high';
  actionUrl?: string;
  actionText?: string;
}

const StudentNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'course',
      title: 'Nouveau chapitre disponible',
      message: 'Le chapitre 5 "Fonctions avancées" du cours JavaScript est maintenant disponible.',
      timestamp: '2024-01-27T10:30:00',
      isRead: false,
      priority: 'medium',
      actionUrl: '/elearning/course/js-101/chapter/5',
      actionText: 'Voir le chapitre'
    },
    {
      id: '2',
      type: 'achievement',
      title: 'Félicitations ! Nouveau badge obtenu',
      message: 'Vous avez débloqué le badge "Maître JavaScript" en terminant tous les exercices.',
      timestamp: '2024-01-26T16:45:00',
      isRead: false,
      priority: 'high',
      actionUrl: '/elearning/student/achievements',
      actionText: 'Voir mes badges'
    },
    {
      id: '3',
      type: 'message',
      title: 'Nouveau message de Marie Dubois',
      message: 'Votre instructrice a répondu à votre question sur les closures.',
      timestamp: '2024-01-26T14:20:00',
      isRead: true,
      priority: 'medium',
      actionUrl: '/elearning/student/messages',
      actionText: 'Lire le message'
    },
    {
      id: '4',
      type: 'reminder',
      title: 'Session d\'\u00e9tude programmée',
      message: 'N\'oubliez pas votre session d\'\u00e9tude "React Hooks" prévue aujourd\'hui à 15h00.',
      timestamp: '2024-01-26T08:00:00',
      isRead: true,
      priority: 'high',
      actionUrl: '/elearning/student/calendar',
      actionText: 'Voir le planning'
    },
    {
      id: '5',
      type: 'order',
      title: 'Commande confirmée',
      message: 'Votre commande #ORD-2024-001 a été confirmée. Vos cours sont maintenant accessibles.',
      timestamp: '2024-01-25T12:15:00',
      isRead: true,
      priority: 'medium',
      actionUrl: '/elearning/student/orders',
      actionText: 'Voir la commande'
    },
    {
      id: '6',
      type: 'system',
      title: 'Maintenance programmée',
      message: 'Une maintenance est prévue dimanche de 2h à 4h. Les cours pourraient être temporairement indisponibles.',
      timestamp: '2024-01-24T18:00:00',
      isRead: true,
      priority: 'low',
      actionUrl: '/elearning/support',
      actionText: 'Plus d\'infos'
    }
  ]);

  const notificationConfig = {
    course: { 
      label: 'Cours', 
      color: 'bg-blue-500', 
      icon: BookOpen,
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-800'
    },
    achievement: { 
      label: 'Réussite', 
      color: 'bg-yellow-500', 
      icon: Award,
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-800'
    },
    message: { 
      label: 'Message', 
      color: 'bg-green-500', 
      icon: MessageCircle,
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-800'
    },
    reminder: { 
      label: 'Rappel', 
      color: 'bg-purple-500', 
      icon: Calendar,
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-800'
    },
    order: { 
      label: 'Commande', 
      color: 'bg-orange-500', 
      icon: ShoppingBag,
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      borderColor: 'border-orange-200 dark:border-orange-800'
    },
    system: { 
      label: 'Système', 
      color: 'bg-gray-500', 
      icon: Info,
      bgColor: 'bg-gray-50 dark:bg-gray-900/20',
      borderColor: 'border-gray-200 dark:border-gray-800'
    }
  };

  const priorityConfig = {
    low: { color: 'text-gray-500', bg: 'bg-gray-100' },
    medium: { color: 'text-blue-500', bg: 'bg-blue-100' },
    high: { color: 'text-red-500', bg: 'bg-red-100' }
  };

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    setIsLoading(false);
  };



  const handleMarkAsRead = (notificationId: string) => {
    setNotifications(prev => prev.map(notif => 
      notif.id === notificationId ? { ...notif, isRead: true } : notif
    ));
    
    toast({
      title: 'Notification marquée comme lue',
      description: 'La notification a été marquée comme lue.'
    });
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
    
    toast({
      title: 'Toutes les notifications marquées comme lues',
      description: 'Toutes vos notifications ont été marquées comme lues.'
    });
  };

  const handleDeleteNotification = (notificationId: string) => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    
    toast({
      title: 'Notification supprimée',
      description: 'La notification a été supprimée avec succès.'
    });
  };

  const handleNotificationAction = (notification: Notification) => {
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    const matchesReadFilter = 
      filter === 'all' ||
      (filter === 'unread' && !notif.isRead) ||
      (filter === 'read' && notif.isRead);
    
    const matchesTypeFilter = typeFilter === 'all' || notif.type === typeFilter;
    
    return matchesReadFilter && matchesTypeFilter;
  });

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Il y a quelques minutes';
    } else if (diffInHours < 24) {
      return `Il y a ${Math.floor(diffInHours)} heure${Math.floor(diffInHours) > 1 ? 's' : ''}`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const typeFilters = Object.keys(notificationConfig);

  return (
    <StudentLayout
      title="Notifications"
      description={`Restez informé de vos activités d'apprentissage${unreadCount > 0 ? ` (${unreadCount} non lue${unreadCount > 1 ? 's' : ''})` : ''}`}
      onStudentProfileLoaded={handleStudentProfileLoaded}
      headerContent={
        unreadCount > 0 && (
          <Button
            onClick={handleMarkAllAsRead}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <CheckCircle className="h-4 w-4" />
            <span>Tout marquer comme lu</span>
          </Button>
        )
      }
    >
      <SEOMetaTags 
        title="Notifications - E-Learning"
        description="Consultez toutes vos notifications et restez informé de vos activités d'apprentissage."
        keywords="notifications, alertes, messages, e-learning"
      />

      <div className="p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0 w-full">
            <div className="flex items-center space-x-4">
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white">
                  {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </div>
        </motion.div>

        {/* Filtres */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full">
            {[
              { key: 'all', label: 'Toutes' },
              { key: 'unread', label: 'Non lues' },
              { key: 'read', label: 'Lues' }
            ].map(filterOption => (
              <Button
                key={filterOption.key}
                variant={filter === filterOption.key ? 'default' : 'outline'}
                size="sm" className="w-full sm:w-auto"
                onClick={() => setFilter(filterOption.key as any)}
              >
                {filterOption.label}
              </Button>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full">
            <Button
              variant={typeFilter === 'all' ? 'default' : 'outline'}
              size="sm" className="w-full sm:w-auto"
              onClick={() => setTypeFilter('all')}
            >
              Tous les types
            </Button>
            {typeFilters.map(type => {
              const config = notificationConfig[type as keyof typeof notificationConfig];
              return (
                <Button
                  key={type}
                  variant={typeFilter === type ? 'default' : 'outline'}
                  size="sm" className="w-full sm:w-auto"
                  onClick={() => setTypeFilter(type)}
                >
                  {config.label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Liste des notifications */}
        <div className="space-y-4">
          {filteredNotifications.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Aucune notification
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {filter === 'unread' ? 'Vous n\'avez aucune notification non lue.' : 'Vous n\'avez aucune notification.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredNotifications.map((notification, index) => {
              const config = notificationConfig[notification.type as keyof typeof notificationConfig];
              const IconComponent = config.icon;
              
              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={`transition-all duration-200 hover:shadow-lg ${!notification.isRead ? 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className={`p-2 rounded-full ${config.color}`}>
                          <IconComponent className="h-5 w-5 text-white" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                              {notification.title}
                            </h3>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {formatTime(notification.timestamp)}
                            </span>
                          </div>
                          
                          <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {notification.message}
                          </p>
                          
                          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                            {notification.actionUrl && (
                              <Button
                                size="sm" className="w-full sm:w-auto"
                                onClick={() => handleNotificationAction(notification)}
                              >
                                {notification.actionText || 'Voir'}
                              </Button>
                            )}
                            
                            {!notification.isRead && (
                              <Button
                                variant="outline"
                                size="sm" className="w-full sm:w-auto"
                                onClick={() => handleMarkAsRead(notification.id)}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            )}
                            
                            <Button
                              variant="outline"
                              size="sm" className="w-full sm:w-auto"
                              onClick={() => handleDeleteNotification(notification.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default StudentNotificationsPage;